/**
 * useTasksList Hook
 * Fetches all tasks for dropdown selection
 */

import { useState, useEffect } from 'react';
import { workflowService, TaskDropdownItem } from '@/services/features/WorkflowService';
import { useAuth } from '@/contexts/AuthContext';

export interface UseTasksListResult {
  tasks: TaskDropdownItem[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useTasksList = (): UseTasksListResult => {
  const { hasuraToken } = useAuth();
  const [tasks, setTasks] = useState<TaskDropdownItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTasks = async () => {
    if (!hasuraToken) {
      setError('Authentication required');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await workflowService.getAllTasksForDropdown(hasuraToken);
      
      if (response.success) {
        setTasks(response.data);
      } else {
        setError(response.error || 'Failed to fetch tasks');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch tasks');
      console.error('Error fetching tasks:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, [hasuraToken]);

  return {
    tasks,
    loading,
    error,
    refetch: fetchTasks
  };
};
